import React, { ReactNode } from 'react';
import { Button, IconButton } from '@fiverr-private/button';
import { PlusIcon } from '@fiverr-private/visuals';
import { getMiraRecruiterEntry } from '@fiverr-private/dm_brief_creation_triggers';
import { getContext } from '@fiverr-private/fiverr_context';
import { loadable } from '@fiverr-private/perseus';
import { PAGE_NAME } from '../../../../configuration/constants';
import { AppI18n, appTranslate } from '../../i18n';
import { reportButtonClick } from '../shared/bqEvents';
import { getMiraBriefLobbyButtonVariant } from './utils';

const CREATE_BRIEF_COPY = 'lobby.create_new_brief_mira';

// Own chunk: only buyers in groups B-D download @fiverr-private/mira_entry_points (SSR renders it for them).
const MiraBriefLobbyButton = loadable(
  () => import(/* webpackChunkName: "MiraBriefLobbyButton" */ './MiraBriefLobbyButton')
);

interface CreateBriefButtonProps {
  title?: ReactNode;
  isIconButton?: boolean;
  /** Run the Mira entry points experiment. Lobby header only: MiraBriefLobbyButton is built for that slot. */
  withMiraEntryPointsExperiment?: boolean;
}

export const CreateBriefButton = ({
  title,
  isIconButton = false,
  withMiraEntryPointsExperiment = false,
}: CreateBriefButtonProps) => {
  const { abTests, userId } = getContext();
  const miraBriefLobbyButtonVariant = withMiraEntryPointsExperiment ? getMiraBriefLobbyButtonVariant(abTests) : null;

  const reportCreateBriefClick = () => {
    reportButtonClick('create_brief');
  };

  // Mira entry points experiment, groups B-D: Mira's own lobby button. It fires its own BI and opens
  // Mira in the same tab.
  if (miraBriefLobbyButtonVariant) {
    return (
      <MiraBriefLobbyButton
        variant={miraBriefLobbyButtonVariant}
        source={PAGE_NAME}
        cohort="experiment"
        guest={!userId}
        onClick={reportCreateBriefClick}
      />
    );
  }

  // Control / not allocated: open the Recruiter (MIRA) entry in a NEW TAB (the lobby stays put).
  // Host picked by environment (prod vs dev).
  const onCreateClick = () => {
    reportCreateBriefClick();
    getMiraRecruiterEntry({ source: PAGE_NAME, ...(userId ? { buyer: String(userId) } : {}) }).openMiraTab();
  };

  return isIconButton ? (
    <IconButton intent="primary" aria-label={appTranslate(CREATE_BRIEF_COPY) as string} onClick={onCreateClick}>
      <PlusIcon />
    </IconButton>
  ) : (
    <Button onClick={onCreateClick}>{title || <AppI18n k={CREATE_BRIEF_COPY} />}</Button>
  );
};
